import React, { FC, useState } from 'react';
import {IProductProps} from '../cors/reducers';
import { useIntl } from 'react-intl';
import {setDiscountToInactive} from '../cors/_requests'
import SharedData from "../../common/SharedData";
import '../../../stylesheet/ProductDicount.css'


const ProductDiscountTab: FC<IProductProps> = ({ prodState }) => {
  const { formatMessage } = useIntl();
  const {currentUser, language} = SharedData()

  // Mark specials stopped in this session so we can reflect it in place —
  // no full-page reload / navigation. Declared before any early return so the
  // hooks run in a stable order on every render (rules-of-hooks).
  const [stoppedIds, setStoppedIds] = useState<Set<string>>(new Set());
  const [stoppingId, setStoppingId] = useState<string | null>(null);

  var special_data = prodState.product_specials;
  var salePrice = Number(prodState.salePrice.value);
  var currentDateTime = new Date();

  if (!Array.isArray(special_data)) {
    return null;
  }

  // Parse a stored special datetime (MySQL DATETIME "YYYY-MM-DD HH:mm:ss" or the
  // ISO-style "YYYY-MM-DDTHH:mm:ss" the HourOnlyInput emits) into a real instant
  // in local time. Returns null for empty / NULL_DATE / unparseable values.
  const parseInstant = (value: string): Date | null => {
    const raw = String(value ?? '').trim();
    if (raw === '' || raw.startsWith('0000-00-00')) {
      return null;
    }
    // Normalise the space form to ISO; no zone suffix => parsed as local time,
    // consistent with `currentDateTime = new Date()` (also local).
    const parsed = new Date(raw.replace(' ', 'T'));
    return isNaN(parsed.getTime()) ? null : parsed;
  };

  const isDiscountActive = (startDateTime: string, endDateTime: string, quantity: number) => {
    try {
      // Compare against the current INSTANT, not the day. The special datetimes
      // carry the sub-day hour captured by HourOnlyInput, and the backend resolver
      // (OfferEffectivePriceService::pickEffective) treats a special as active iff
      // `date_start <= now AND date_end > now` — start INCLUSIVE, end EXCLUSIVE.
      const now = currentDateTime;
      const start = parseInstant(startDateTime);
      const end = parseInstant(endDateTime);

      // Match the backend: unlimited (-1) or positive quantity counts.
      const hasQuantity = quantity > 0 || quantity === -1;
      if (!hasQuantity || start === null) {
        return formatMessage({ id: 'not_active' });
      }

      const started = start <= now;
      // End-EXCLUSIVE: a null/open end never expires; otherwise active only while now < end.
      const notEnded = end === null || now < end;

      if (started && notEnded) {
        return formatMessage({ id: 'active_now' });
      }
      if (!started) {
        // Scheduled — starts in the future.
        return formatMessage({ id: 'active' });
      }
      return formatMessage({ id: 'not_active' });
    } catch (error) {
      console.error('Error parsing dates:', error);
      return formatMessage({ id: 'not_active' });
    }
  };

  const calculateDiscount = (price: number):number => {
    const discountPercentage = Math.round(((salePrice - price) / salePrice) * 100);
    return discountPercentage;
  };

  const handleButtonClick = async (product_special_id: string) => {
    setStoppingId(product_special_id);
    try {
      await setDiscountToInactive(currentUser?.token, language, product_special_id);
      setStoppedIds((prev) => new Set(prev).add(product_special_id));
    } catch (error) {
      console.error('Error stopping special:', error);
    } finally {
      setStoppingId(null);
    }
  };

  return (
      <>
        <div>
          <table className='align-middle border-bottom-1 gs-0 gy-5 table edit-width'>
            <thead>
            <tr>
              <th style={{ padding: '20px' }} className='fw-bold text-uppercase'>{formatMessage({ id: 'percentage' })}</th>
              <th className='fw-bold text-uppercase'>{formatMessage({ id: 'date_start' })}</th>
              <th className='fw-bold text-uppercase'>{formatMessage({ id: 'date_end' })}</th>
              <th className='fw-bold text-uppercase'>{formatMessage({ id: 'status' })}</th>
            </tr>
            </thead>
            <tbody style={{ backgroundColor: 'white', padding: '10px' }}>
            {special_data.map((item: any, index: number) => {
              const stopped = stoppedIds.has(item.product_special_id);
              const status = stopped
                ? formatMessage({ id: 'not_active' })
                : isDiscountActive(item.date_start, item.date_end, item.quantity);
              const isStatic =
                status === formatMessage({ id: 'not_active' }) ||
                status === formatMessage({ id: 'active_now_cannot_be_deactivated' });
              const isStopping = stoppingId === item.product_special_id;
              return (
                <tr key={index}>
                  {/*<td>{item.price}</td>*/}
                  <td style={{ padding: '20px' }}>{calculateDiscount(item.price)}</td>
                  <td style={{ padding: '20px' }}>{item.date_start}</td>
                  <td style={{ padding: '20px' }}>{item.date_end}</td>
                  <td style={{ padding: '20px' }}>
                    {isStatic ? (
                        <span style={{ color: 'black', fontWeight: 'bold' }}>{status}</span>
                    ) : (
                        <button
                            style={{ backgroundColor: 'black', color: 'white', padding: '5px', borderRadius: '5px', opacity: isStopping ? 0.6 : 1 }}
                            disabled={isStopping}
                            onClick={() => handleButtonClick(item.product_special_id)}>
                          {status}
                        </button>
                    )}
                  </td>
                </tr>
              );
            })}
            </tbody>
          </table>
        </div>
      </>
  );
};

export default ProductDiscountTab;
